import { AnimatePresence, motion, PanInfo, useDragControls } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Content, ContentVariant } from '@/components/layout/Content';
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { X } from 'lucide-react';

const focusableElementSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

type ActionSheetProps = {
  cancelLabel?: ReactNode;
  children: ReactNode;
  className?: string;
  contentVariant?: ContentVariant;
  footer?: ReactNode | null;
  footerDescription?: ReactNode;
  onCancel?: () => void;
  onOpenChange: (open: boolean) => void;
  onSubmit?: () => void;
  open: boolean;
  submitDisabled?: boolean;
  submitLabel?: ReactNode;
  subtitle?: ReactNode;
  title: ReactNode;
  titleId?: string;
};

const actionSheetHistoryStateKey = '__wlActionSheet';

const getActionSheetHistoryState = (): Record<string, unknown> => {
  const state = window.history.state as Record<string, unknown> | null;

  return {
    ...(state ?? {}),
    [actionSheetHistoryStateKey]: true
  };
};

const hasActionSheetHistoryState = (): boolean => {
  const state = window.history.state as Record<string, unknown> | null;

  return state?.[actionSheetHistoryStateKey] === true;
};

export function ActionSheet({
  cancelLabel = 'Отмена',
  children,
  className,
  contentVariant = 'flow',
  footer,
  footerDescription,
  onCancel,
  onOpenChange,
  onSubmit,
  open,
  submitDisabled,
  submitLabel,
  subtitle,
  title,
  titleId
}: ActionSheetProps) {
  const dragControls = useDragControls();
  const generatedTitleId = useId();
  const hasHistoryEntryRef = useRef(false);
  const isClosingFromPopStateRef = useRef(false);
  const onOpenChangeRef = useRef(onOpenChange);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const sheetTitleId = titleId ?? generatedTitleId;
  const shouldRenderDefaultFooter = footer === undefined;
  onOpenChangeRef.current = onOpenChange;

  const close = useCallback((): void => {
    if (typeof window !== 'undefined' && hasHistoryEntryRef.current) {
      window.history.back();
      return;
    }

    onOpenChangeRef.current(false);
  }, []);

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo): void => {
      if (info.offset.y > 100) {
        close();
      }
    },
    [close]
  );

  useEffect(() => {
    if (!open || typeof document === 'undefined' || typeof window === 'undefined') return;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      const currentPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;

      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    if (!open) {
      if (hasHistoryEntryRef.current && !isClosingFromPopStateRef.current) {
        hasHistoryEntryRef.current = false;
        window.history.back();
        return;
      }

      hasHistoryEntryRef.current = false;
      isClosingFromPopStateRef.current = false;
      return;
    }

    window.history.pushState(getActionSheetHistoryState(), '', window.location.href);
    hasHistoryEntryRef.current = true;
    isClosingFromPopStateRef.current = false;

    const handlePopState = () => {
      hasHistoryEntryRef.current = false;
      isClosingFromPopStateRef.current = true;
      onOpenChangeRef.current(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (
        typeof window === 'undefined' ||
        !hasHistoryEntryRef.current ||
        isClosingFromPopStateRef.current ||
        !hasActionSheetHistoryState()
      ) {
        return;
      }

      hasHistoryEntryRef.current = false;
      window.history.back();
    };
  }, []);

  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') return;

    const sheetElement = sheetRef.current;
    previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const getFocusableElements = (): HTMLElement[] => {
      if (!sheetElement) return [];

      return Array.from(sheetElement.querySelectorAll<HTMLElement>(focusableElementSelector));
    };

    const focusFirstElement = (): void => {
      const firstFocusableElement = getFocusableElements()[0];
      (firstFocusableElement ?? sheetElement)?.focus({ preventScroll: true });
    };

    focusFirstElement();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) {
        e.preventDefault();
        sheetElement?.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (e.shiftKey && (activeElement === firstFocusableElement || !sheetElement?.contains(activeElement))) {
        e.preventDefault();
        lastFocusableElement.focus();
      } else if (!e.shiftKey && (activeElement === lastFocusableElement || !sheetElement?.contains(activeElement))) {
        e.preventDefault();
        firstFocusableElement.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent): void => {
      if (!sheetElement?.contains(event.target as Node)) focusFirstElement();
    };

    document.addEventListener('focusin', handleFocusIn);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('keydown', handleKeyDown);
      const previouslyFocusedElement = previouslyFocusedElementRef.current;

      window.setTimeout(() => {
        previouslyFocusedElement?.focus();
      });
      previouslyFocusedElementRef.current = null;
    };
  }, [close, open]);

  const handleCancel = (): void => {
    if (onCancel) {
      onCancel();
      return;
    }

    close();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-[hsl(var(--overlay)/0.6)]"
          data-action-sheet-overlay="true"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          key="action-sheet-overlay"
          onClick={close}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        />
      ) : null}

      {open ? (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          aria-labelledby={sheetTitleId}
          aria-modal="true"
          className="fixed inset-x-0 bottom-0 z-50 pointer-events-none"
          drag="y"
          dragConstraints={{ bottom: 200, top: 0 }}
          dragControls={dragControls}
          dragElastic={{ bottom: 0.3, top: 0 }}
          dragListener={false}
          exit={{ y: '100%' }}
          initial={{ opacity: 0, y: 100 }}
          key="action-sheet-dialog"
          onDragEnd={handleDragEnd}
          ref={sheetRef}
          role="dialog"
          tabIndex={-1}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
            <Content className="max-[768px]:px-0" variant={contentVariant}>
              <div
                className={cn(
                  'pointer-events-auto flex max-h-[85vh] flex-col overflow-hidden',
                  'max-[768px]:max-h-[calc(100dvh_-_env(safe-area-inset-top))]',
                  'rounded-t-3xl border-t border-border bg-surface-raised pb-[env(safe-area-inset-bottom)] min-[769px]:pb-0',
                  className
                )}
              >
                <div
                  className="shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
                  onPointerDown={(e) => dragControls.start(e)}
                >
                  <div className="flex justify-center pt-2 pb-1">
                    <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
                  </div>
                </div>
                <div className="flex items-center justify-between border-b border-border">
                  <div className="flex w-full items-center justify-between gap-3 px-4 pb-3 pt-0">
                    <div className="min-w-0 flex-1">
                      {subtitle && (
                        <p className="truncate font-sans text-xs leading-4 text-muted-foreground">
                          {subtitle}
                        </p>
                      )}
                      <h2
                        className="truncate font-sans text-lg font-semibold leading-6 text-foreground"
                        id={sheetTitleId}
                      >
                        {title}
                      </h2>
                    </div>
                    <button
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label="Закрыть"
                      onClick={close}
                      type="button"
                    >
                      <X
                        className="size-5"
                        strokeWidth={2}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">{children}</div>

                {footer !== null && (
                  <div className="shrink-0 p-4 border-t border-border-strong">
                    {shouldRenderDefaultFooter ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-4">
                          <Button
                            className="flex-1"
                            onClick={handleCancel}
                            size="md"
                            type="button"
                            variant="secondary"
                          >
                            {cancelLabel}
                          </Button>
                          <Button
                            className="flex-1"
                            disabled={submitDisabled}
                            onClick={onSubmit}
                            size="md"
                            type="button"
                          >
                            {submitLabel}
                          </Button>
                        </div>
                        {footerDescription && (
                          <div className="text-center text-xs text-muted-foreground">
                            {footerDescription}
                          </div>
                        )}
                      </div>
                    ) : (
                      footer
                    )}
                  </div>
                )}
              </div>
            </Content>
          </motion.div>
      ) : null}
    </AnimatePresence>
  );
}